import { useContext, useMemo, useState } from 'react'
import { DefaultButton, IconButton, Spinner, SpinnerSize, Stack, Text } from '@fluentui/react'

import { ChatHistoryLoadingState, CosmosDBStatus, schoolAuthLogout } from '../../api'
import { AppStateContext } from '../../state/AppProvider'
import { t } from '../../i18n'
import ChatHistoryList from '../ChatHistory/ChatHistoryList'

import styles from './PersonalSidebar.module.css'

type PersonalSidebarProps = {
  onNewChat: () => void
  isOpen?: boolean
  onClose?: () => void
}

const firstValue = (objects: Array<Record<string, unknown> | null | undefined>, keys: string[]) => {
  for (const object of objects) {
    if (!object) continue
    for (const key of keys) {
      const value = object[key]
      if (typeof value === 'string' && value.trim()) return value.trim()
    }
  }
  return ''
}

export function PersonalSidebar({ onNewChat, isOpen = true, onClose }: PersonalSidebarProps) {
  const appStateContext = useContext(AppStateContext)
  const [loggingOut, setLoggingOut] = useState(false)
  const profile = appStateContext?.state.schoolAuth
  const language = appStateContext?.state.language || 'zh-TW'
  const student = profile?.student && typeof profile.student === 'object' ? profile.student : null
  const displayName = useMemo(
    () => firstValue([profile, student], ['display_name', 'displayName', 'chiName', 'name', 'studentName', 'userName', '姓名']) || t(language, 'defaultUser'),
    [language, profile, student]
  )
  const accountType = useMemo(() => {
    const declaredType = firstValue([profile, student], ['account_type', 'accounType', 'accountType', 'type']).toLowerCase()
    if (declaredType === 'staff' || declaredType === 'student') return declaredType

    const staffId = firstValue([student, profile], ['staffId', 'staffNo', 'employeeId'])
    const studentNo = firstValue([student, profile], ['student_id', 'stdNo', 'stuNo', 'studentNo', 'studentNumber'])
    if (staffId && !studentNo) return 'staff'
    if (studentNo && !staffId) return 'student'
    return 'unknown'
  }, [profile, student])
  const isStaff = accountType === 'staff'
  const identityLabel = isStaff ? t(language, 'staffId') : t(language, 'studentId')
  const identityNumber = useMemo(
    () => firstValue(
      [profile, student],
      isStaff
        ? ['identity_no', 'staffId', 'staffNo', 'employeeId', '工號']
        : ['identity_no', 'student_id', 'stdNo', 'stu_no', 'stuNo', 'studentNo', 'studentNumber', '學號']
    ) || '—',
    [isStaff, profile, student]
  )
  const department = useMemo(
    () => firstValue([profile, student], ['department', 'deptName', 'departmentName', '系所', '單位']),
    [profile, student]
  )

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      await schoolAuthLogout()
    } finally {
      window.location.assign('/')
    }
  }

  return (
    <aside className={`${styles.sidebar} ${!isOpen ? styles.sidebarClosed : ''}`} aria-label={t(language, 'personalSidebar')}>
      <section className={styles.profileCard} aria-label={t(language, 'personalSidebar')}>
        {onClose && (
          <IconButton
            className={styles.mobileCloseButton}
            iconProps={{ iconName: 'Cancel' }}
            title={t(language, 'closeSidebar')}
            aria-label={t(language, 'closeSidebar')}
            onClick={onClose}
          />
        )}
        <div className={styles.profileHeading}>
          <div className={styles.avatar} aria-hidden="true">{displayName.slice(0, 1)}</div>
          <div className={styles.profileText}>
            <Text className={styles.profileName}>{displayName}</Text>
            <Text className={styles.profileStudentNo}>{identityLabel}：{identityNumber}</Text>
            {department && <Text className={styles.profileDepartment}>{department}</Text>}
          </div>
        </div>
        <DefaultButton
          className={styles.logoutButton}
          text={loggingOut ? t(language, 'loggingOut') : t(language, 'logout')}
          iconProps={{ iconName: 'SignOut' }}
          onClick={handleLogout}
          disabled={loggingOut}
        />
      </section>

      <div className={styles.historyHeader}>
        <Text role="heading" aria-level={2}>{t(language, 'history')}</Text>
        <IconButton
          iconProps={{ iconName: 'Add' }}
          title={t(language, 'newChat')}
          aria-label={t(language, 'newChat')}
          onClick={onNewChat}
        />
      </div>

      <section className={styles.historyContent} aria-label={t(language, 'history')}>
        {appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading && (
          <Stack horizontalAlign="center" className={styles.statusMessage}>
            <Spinner size={SpinnerSize.medium} label={t(language, 'loadingHistory')} />
          </Stack>
        )}
        {appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Success &&
          appStateContext.state.isCosmosDBAvailable.cosmosDB && <ChatHistoryList disableInternalScroll />}
        {appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail && (
          <Text className={styles.statusMessage}>{t(language, 'historyUnavailable')}</Text>
        )}
        {appStateContext?.state.isCosmosDBAvailable.status === CosmosDBStatus.NotConfigured && (
          <Text className={styles.statusMessage}>{t(language, 'historyNotEnabled')}</Text>
        )}
      </section>
    </aside>
  )
}
